import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import { TripType } from "@b2bportal/air-shopping-api";
import {
  FlightRawEventName,
  FlightShopSelectors,
  useEmitCoreFlightRawEventOnce,
} from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import {
  CoreFlightSteps,
  FlightsNavigationFlow,
  useDeviceType,
} from "@b2bportal/core-utilities";

import { useI18nContext } from "@hopper-b2b/i18n";
import { ReviewBreakdown, ReviewFare } from "@tenant/flights";
import { useValidateFlightShopRoute } from "@tenant/flights";
import { Page } from "@tenant/ui";
import clsx from "clsx";
import {
  getAirports,
  getSelectedFarePaxPricing,
  getSelectedTripDetailsAndFareDetails,
} from "../../features/shop/store/selectors";
import defaultStyles from "./ReviewFarePage.module.scss";

export type ReviewFarePageComponentProps = PlatformComponentProps;

export const ReviewFarePage = ({
  children,
  className,
}: ReviewFarePageComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.ReviewFarePage;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const currentStep = CoreFlightSteps.REVIEW_FARE;
  const valid = useValidateFlightShopRoute(currentStep);
  const goToNextStep = FlightsNavigationFlow.useGoToNextStep(currentStep);

  const navigate = useNavigate();
  const { isDesktopAndUp } = useDeviceType();
  useEffect(() => {
    if (isDesktopAndUp) {
      navigate(-1);
    }
  }, [isDesktopAndUp, navigate]);

  const { t } = useI18nContext();

  const { tripDetails, fareDetails } = useSelector(
    getSelectedTripDetailsAndFareDetails
  );
  const airports = useSelector(getAirports);
  const paxPricing = useSelector(getSelectedFarePaxPricing);

  const isOutgoingMultiTicket = useSelector(
    FlightShopSelectors.isOutgoingMultiTicket
  );
  const isReturnMultiTicket = useSelector(
    FlightShopSelectors.isReturnMultiTicket
  );
  const isMultiTicket = isOutgoingMultiTicket || isReturnMultiTicket;

  useEmitCoreFlightRawEventOnce({
    name: FlightRawEventName.ViewedFareDetails,
  });

  if (!valid || paxPricing == null || fareDetails == null) return null;

  return (
    <Page className={clsx(block, className)}>
      <div className={cn("container")}>
        <div className={cn("header")}>
          <h2 className={cn("title")}>
            {isMultiTicket
              ? t("flightReview.multiTicketTitle")
              : t("flightReview.nonMultiTicketTitle")}
          </h2>
          <div className={cn("subtitle")}>
            {isMultiTicket
              ? t("flightReview.multiTicketSubtitle")
              : t("flightReview.nonMultiTicketSubtitle")}
          </div>
        </div>
        <div className={cn("review-fare-container")}>
          <ReviewFare
            tripDetails={tripDetails}
            sliceIndex={0}
            fareDetails={fareDetails}
            className={cn("review-fare")}
            airports={airports}
            key={"review-fare_0"}
          />
          {tripDetails.slices[1] != null ? (
            <ReviewFare
              tripDetails={tripDetails}
              sliceIndex={1}
              fareDetails={fareDetails}
              airports={airports}
              className={cn("review-fare")}
              key={"review-fare_1"}
            />
          ) : null}
        </div>
        <div className={cn("secondary-content")}>
          <ReviewBreakdown
            passengerPricing={paxPricing}
            tripType={
              tripDetails.slices.length > 1
                ? TripType.round_trip
                : TripType.one_way
            }
            className={cn("review-breakdown")}
          />
          <button
            className={cn("continue-button")}
            onClick={() => {
              goToNextStep();
            }}
          >
            {t("continue")}{" "}
          </button>
        </div>
        {children}
      </div>
    </Page>
  );
};
